// Data hooks of the sharing screens: the generated client carries the calls (cookies, CSRF,
// problem+json and the step-up replay are the fetcher's); these hooks keep the caches in step and
// add the one read the generator cannot express (the audit log by cursor).
import {
  getMySpaceInvitationsControllerListQueryKey,
  getMySpacesControllerListQueryKey,
  getSpaceApprovalsControllerListQueryKey,
  getSpacesControllerGetQueryKey,
  revokeAccessControllerAudit,
  useMembersControllerList,
  useMySpaceInvitationsControllerList,
  useOrgSharingControllerLookup,
  useOrgSharingControllerPartnerships,
  useRevokeAccessControllerClose,
  useRevokeAccessControllerRemoveMember,
  useRevokeAccessControllerRemovePartner,
  useSpaceApprovalsControllerApprove,
  useSpaceApprovalsControllerDecline,
  useSpaceApprovalsControllerList,
  useSpaceChannelsControllerList,
  useSpaceInvitationAnswersControllerAccept,
  useSpaceInvitationAnswersControllerDecline,
  useSpaceInvitationsControllerCreate,
  useSpaceInvitationsControllerExtend,
  useSpaceInvitationsControllerList,
  useSpaceInvitationsControllerRevoke,
  useSpacePartnersControllerDelegate,
  useSpacePartnersControllerInvite,
  useSpacePartnersControllerList,
  useSpacePartnersControllerMembers,
  type SpaceAuditPageDtoOutput,
} from '@batiflow/api-client';
import { useInfiniteQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRefreshSession } from '@/shared/hooks/use-refresh-session';
import { AUDIT_PAGE_SIZE } from './model';

/** The people the picker of a delegate offers: the active members of the organisation. */
const PICKER_SIZE = 200;

// --- Reads --------------------------------------------------------------------------------------

/** The partners of a space (host managers only: the caller passes `enabled`). */
export const usePartners = (spaceId: string, enabled = true) =>
  useSpacePartnersControllerList(spaceId, { query: { enabled } });

/** The invitations of a space (host managers only). */
export const useSpaceInvitations = (spaceId: string, enabled = true) =>
  useSpaceInvitationsControllerList(spaceId, { query: { enabled } });

/** The people with access to the space; `partnerId` limits it to one partnership's delegates. */
export const useSpaceMembers = (spaceId: string, partnerId?: string, enabled = true) =>
  useSpacePartnersControllerMembers(spaceId, partnerId ? { partnerId } : undefined, {
    query: { enabled },
  });

/** The channels of the space (the scope picker). */
export const useSpaceChannels = (spaceId: string, enabled = true) =>
  useSpaceChannelsControllerList(spaceId, undefined, { query: { enabled } });

/** The organisation behind a tax id: the host asks, then confirms the name it gets back. */
export const usePartnerLookup = (orgId: string, taxId: string | null) =>
  useOrgSharingControllerLookup(
    orgId,
    { taxId: taxId ?? '' },
    { query: { enabled: taxId !== null, retry: false } },
  );

/** The partnerships of an organisation (its owners and administrators). */
export const usePartnerships = (orgId: string, enabled = true) =>
  useOrgSharingControllerPartnerships(orgId, { query: { enabled } });

/** What waits for the caller: invitations to spaces and partnership requests of its organisations. */
export const useIncomingInvitations = () => useMySpaceInvitationsControllerList();

/** The requests waiting for the organisation's approval (`space.approve_external_membership`). */
export const useApprovals = (orgId: string, enabled = true) =>
  useSpaceApprovalsControllerList(orgId, { query: { enabled } });

/** The active members of the organisation: the people a partner admin can delegate. */
export const useOrganisationPeople = (orgId: string) =>
  useMembersControllerList(orgId, { status: 'active', limit: PICKER_SIZE });

/** The audit log of a space, page by page ("Charger plus"). */
export function useSpaceAudit(spaceId: string) {
  return useInfiniteQuery({
    queryKey: [`/api/v1/spaces/${spaceId}/audit-events`, 'pages'],
    queryFn: ({ pageParam, signal }): Promise<SpaceAuditPageDtoOutput> =>
      revokeAccessControllerAudit(
        spaceId,
        { limit: AUDIT_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
}

// --- Refreshing ---------------------------------------------------------------------------------

const SHARING_PATHS = ['partners', 'members', 'invitations', 'audit-events'] as const;

function refreshSharing(queryClient: QueryClient, spaceId: string): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: [key] }) =>
      typeof key === 'string' &&
      SHARING_PATHS.some((path) => key.startsWith(`/api/v1/spaces/${spaceId}/${path}`)),
  });
}

/** Refreshes the partners, people, invitations and audit log of a space. */
export function useRefreshSharing(spaceId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => refreshSharing(queryClient, spaceId);
}

// --- Host writes --------------------------------------------------------------------------------

export function useInvitePartner(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  return useSpacePartnersControllerInvite({ mutation: { onSuccess: refresh } });
}

export function useRemovePartner(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  const queryClient = useQueryClient();
  return useRevokeAccessControllerRemovePartner({
    mutation: {
      onSuccess: async () => {
        await refresh();
        // A partner admin who leaves loses the space: its lists forget it.
        await queryClient.invalidateQueries({ queryKey: getMySpacesControllerListQueryKey() });
      },
    },
  });
}

export function useRemoveMember(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  return useRevokeAccessControllerRemoveMember({ mutation: { onSuccess: refresh } });
}

export function useExtendMember(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  return useSpaceInvitationsControllerExtend({ mutation: { onSuccess: refresh } });
}

export function useCreateInvitation(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  return useSpaceInvitationsControllerCreate({ mutation: { onSuccess: refresh } });
}

export function useRevokeInvitation(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  return useSpaceInvitationsControllerRevoke({ mutation: { onSuccess: refresh } });
}

/** Closing the space: the space itself, its lists and its sharing screens are reloaded. */
export function useCloseSpace(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  const queryClient = useQueryClient();
  return useRevokeAccessControllerClose({
    mutation: {
      onSuccess: async (space) => {
        queryClient.setQueryData(getSpacesControllerGetQueryKey(spaceId), space);
        await queryClient.invalidateQueries({ queryKey: getMySpacesControllerListQueryKey() });
        await refresh();
      },
    },
  });
}

// --- Partner writes -----------------------------------------------------------------------------

export function useDelegateMember(spaceId: string) {
  const refresh = useRefreshSharing(spaceId);
  const queryClient = useQueryClient();
  return useSpacePartnersControllerDelegate({
    mutation: {
      onSuccess: async () => {
        await refresh();
        // The partnership's member count moves with the delegation.
        await queryClient.invalidateQueries({
          predicate: ({ queryKey: [key] }) =>
            typeof key === 'string' && key.endsWith('/partnerships'),
        });
      },
    },
  });
}

// --- Answers (the person invited, or the partner admin) ------------------------------------------

/** Accept and decline refresh the waiting list, the spaces and the session (new access). */
export function useAnswerInvitation() {
  const queryClient = useQueryClient();
  const refreshSession = useRefreshSession();
  const settle = async () => {
    await queryClient.invalidateQueries({
      queryKey: getMySpaceInvitationsControllerListQueryKey(),
    });
    await queryClient.invalidateQueries({ queryKey: getMySpacesControllerListQueryKey() });
    await queryClient.invalidateQueries({
      predicate: ({ queryKey: [key] }) => typeof key === 'string' && key.endsWith('/partnerships'),
    });
    // /me lists the external spaces: a new access shows in the header at once.
    await refreshSession().catch(() => undefined);
  };
  const accept = useSpaceInvitationAnswersControllerAccept({ mutation: { onSuccess: settle } });
  const decline = useSpaceInvitationAnswersControllerDecline({ mutation: { onSuccess: settle } });
  return { accept, decline };
}

// --- Home-organisation approvals ----------------------------------------------------------------

export function useDecideApproval(orgId: string) {
  const queryClient = useQueryClient();
  const settle = () =>
    queryClient.invalidateQueries({ queryKey: getSpaceApprovalsControllerListQueryKey(orgId) });
  const approve = useSpaceApprovalsControllerApprove({ mutation: { onSuccess: settle } });
  const decline = useSpaceApprovalsControllerDecline({ mutation: { onSuccess: settle } });
  return { approve, decline };
}
